import { decoratorWithParams } from '@ember-decorators/utils/decorator';

const extendableProperties = new Map();

/**
 * Registers a property as extendable for a given class name.
 */
function registerExtendableByName(propName: string, className = 'Unknown') {
  if (!extendableProperties.has(className)) {
    extendableProperties.set(className, new Set());
  }
  extendableProperties.get(className).add(propName);
}

/**
 * Registers a property as extendable using a class prototype.
 */
function registerExtendable(target: object, key: string) {
  const className = target.constructor?.name ?? 'Unknown';
  registerExtendableByName(key, className);
}

/**
 * Decorator usage: @extendable
 */
const extendable = decoratorWithParams(
  (target: object, key: string, descriptor: unknown) => {
    registerExtendable(target, key);
    return descriptor;
  },
  'extendable'
);

/**
 * Classic class usage: wrap a computed property and register it as extendable.
 *
 * Example:
 *   someProp: makeExtendable('someProp', computed('otherProp', function() { ... }))
 */
function makeExtendable(propName: string, computedProp: object) {
  // Delay registration to runtime if needed
  // You may want to pass actual class name for stricter matching
  registerExtendableByName(propName);
  return computedProp;
}

/**
 * Check whether a property is marked as extendable for a given instance/class.
 */
function isExtendable(target: object, key: string) {
  const className = target.constructor?.name ?? 'Unknown';
  return extendableProperties.get(className)?.has(key);
}

export { extendable, makeExtendable, isExtendable, extendableProperties };
